/**
 * App-wide client wiring: the API client, its token persistence, and the TanStack Query
 * cache policy.
 */

import { ApiError, SaySoonerClient, type TokenPair, type TokenStore } from '@saysooner/api-client';
import { QueryClient } from '@tanstack/react-query';
import { IndexedDbStore } from './offline-store.js';

const TOKEN_KEY = 'saysooner.tokens';
const DEVICE_KEY = 'saysooner.deviceId';

/**
 * Token persistence. On web the access token lives in memory and the refresh token in
 * `localStorage`; a production deployment behind the same origin upgrades this to an
 * httpOnly cookie (docs/07-tech-architecture.md §11).
 */
export class WebTokenStore implements TokenStore {
  private cached: TokenPair | null = null;

  async get(): Promise<TokenPair | null> {
    if (this.cached !== null) return this.cached;
    try {
      const raw = globalThis.localStorage?.getItem(TOKEN_KEY);
      if (raw === null || raw === undefined) return null;
      this.cached = JSON.parse(raw) as TokenPair;
      return this.cached;
    } catch {
      return null;
    }
  }

  async set(tokens: TokenPair | null): Promise<void> {
    this.cached = tokens;
    try {
      if (tokens === null) globalThis.localStorage?.removeItem(TOKEN_KEY);
      else globalThis.localStorage?.setItem(TOKEN_KEY, JSON.stringify(tokens));
    } catch {
      // A private-mode browser with storage disabled still works for this session.
    }
  }
}

/**
 * The one token store the app uses.
 *
 * A singleton because the media player needs the same access token the API client is
 * using: a learner's own recording is served from a private R2 prefix and an `<audio>`
 * element cannot send an Authorization header, so that one file has to be fetched and
 * handed to the player as bytes (`lib/media.ts`).
 */
export const tokenStore = new WebTokenStore();

/**
 * Held for the life of the page, because `localStorage` is not the only thing that has
 * to work for the id to be stable. Where site data is unavailable — a sandboxed frame, a
 * headless renderer, a browser with storage blocked — the setter below throws and an
 * unmemoised generator hands back a different id on every call. `bootstrap()` asks twice
 * (sign-in, then device registration), so that context created a throwaway account per
 * page load: production showed it as pairs of anonymous users seconds apart.
 */
let generatedDeviceId: string | undefined;

/** Stable per-browser device id, which keeps anonymous sign-in idempotent. */
export function deviceId(): string {
  try {
    const existing = globalThis.localStorage?.getItem(DEVICE_KEY);
    if (existing !== null && existing !== undefined && existing.length > 0) return existing;
  } catch {
    // fall through
  }
  if (generatedDeviceId !== undefined) return generatedDeviceId;
  generatedDeviceId = `web-${crypto.randomUUID()}`;
  try {
    globalThis.localStorage?.setItem(DEVICE_KEY, generatedDeviceId);
  } catch {
    // fall through
  }
  return generatedDeviceId;
}

export interface AppClients {
  readonly api: SaySoonerClient;
  readonly queryClient: QueryClient;
}

export function createClients(baseUrl: string): AppClients {
  const api = new SaySoonerClient({
    baseUrl,
    tokens: tokenStore,
    // Content the learner has seen, and anything they download, stays available when
    // the network is not. `Offline lessons` is sold on the paywall; this is the half
    // of it that is the client's.
    offline: new IndexedDbStore(),
    onUnauthenticated: () => {
      // The learner is shown the value carousel again; nothing is lost, because an
      // anonymous account is recreated on the next launch.
      globalThis.location?.assign('/');
    },
  });

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // Learning state changes on the learner's own actions, so background refetching
        // is noise. Invalidate explicitly after mutations instead.
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Never retry a limit or an entitlement boundary; they are not failures.
          // A 429 is marked retryable by the server so a client knows it clears, but
          // retrying it straight away is what caused it: three immediate attempts spend
          // three tokens out of the same budget and push the reset further out. The
          // learner is told to slow down and retries when they choose.
          if (typeof error === 'object' && error !== null && 'status' in error) {
            if ((error as { status: number }).status === 429) return false;
          }
          const retryable =
            typeof error === 'object' &&
            error !== null &&
            'retryable' in error &&
            (error as { retryable: boolean }).retryable;
          return retryable === true && failureCount < 2;
        },
      },
      mutations: { retry: 0 },
    },
  });

  return { api, queryClient };
}

export const API_BASE_URL: string =
  (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? 'http://localhost:8787';

/**
 * Turns a media URL the API returned into one a player can actually fetch.
 *
 * Every `audioUrl` comes back root-relative — `/v1/audio/<key>` — which the contract's
 * `MediaUrlSchema` explicitly permits. Relative to *what* is the part nothing was
 * handling: an `<audio src>` resolves it against the page origin, the app is served from
 * Pages and the API is a Worker on another host, so every recording 404ed in any
 * deployed build. It worked in dev only because dev serves both from localhost.
 *
 * Absolute URLs pass through, so moving media to a CDN hostname later needs no change.
 */
export function mediaUrl(path: string | null | undefined): string | null {
  if (path === null || path === undefined || path.length === 0) return null;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE_URL}${path}`;
}

/** Query keys, centralized so invalidation after a mutation is exact. */
export const qk = {
  me: ['me'] as const,
  notifications: ['me', 'notifications'] as const,
  courses: ['courses'] as const,
  today: (courseId: string) => ['today', courseId] as const,
  path: (courseId: string) => ['path', courseId] as const,
  lesson: (lessonId: string) => ['lesson', lessonId] as const,
  session: (sessionId: string) => ['session', sessionId] as const,
  report: (sessionId: string) => ['report', sessionId] as const,
  reviewQueue: (courseId: string, allowSpoken: boolean) =>
    ['review', 'queue', courseId, allowSpoken] as const,
  mistakes: (courseId: string, status: string) => ['mistakes', courseId, status] as const,
  clinic: (courseId: string) => ['clinic', courseId] as const,
  scenarios: (courseId: string, filters: string) => ['scenarios', courseId, filters] as const,
  listening: (courseId: string) => ['listening', courseId] as const,
  progress: (courseId: string, window: string) => ['progress', courseId, window] as const,
  saved: (courseId: string) => ['saved', courseId] as const,
  entitlements: ['entitlements'] as const,
};

/**
 * Drops the dimensions that were not measured.
 *
 * Analytics payloads take `Record<string, number>`, and a null dimension must be absent
 * rather than sent as zero: the cost and quality dashboards average these, and a run of
 * zeroes from sessions nobody could score would read as a collapse in pronunciation
 * accuracy that never happened.
 */
export function measuredScores(
  dimensions: Readonly<Record<string, number | null | undefined>> | null | undefined,
): Record<string, number> | null {
  if (dimensions === null || dimensions === undefined) return null;
  const measured: Record<string, number> = {};
  for (const [key, value] of Object.entries(dimensions)) {
    if (value !== null && value !== undefined) measured[key] = value;
  }
  return measured;
}

/**
 * Props for an `ErrorState`, offering a retry only when retrying could work.
 *
 * The server says so on every problem it sends: `retryable` is in the contract, and
 * `validation`, `unauthenticated`, `forbidden`, `notFound`, `conflict`,
 * `quota-exhausted`, `entitlement-required` and `stale-version` are all false. Screens
 * ignored it and offered "Try again" on all of them. `notFound` alone is thrown from 28
 * places, so every screen that loads something by id could hand the learner a button
 * that cannot work — which is what the leagues screen and the spent-voice-quota screen
 * were both doing before they were found one at a time.
 *
 * The server's own sentence comes with it when it sent one and the caller has not
 * written something better, because "We couldn't load that" explains less than
 * "Sign in to use this. Your progress will be kept."
 */
export function retryIf(
  error: unknown,
  retry: () => void,
): { onRetry?: () => void; detail?: string } {
  if (!(error instanceof ApiError)) return { onRetry: retry };
  const detail = error.problem.detail;
  return {
    ...(error.retryable ? { onRetry: retry } : {}),
    ...(detail === undefined || detail.length === 0 ? {} : { detail }),
  };
}
